import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  Target, CheckCircle, Circle, Plus, Trash2, Calendar
} from "lucide-react";
import { useConsultation } from "../../context/ConsultationContext";

export default function ProgressBar({ expertId, clientId, expertName, isExpertView }) {
  const { getGoals, addGoal, toggleGoal, deleteGoal } = useConsultation();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGoalText, setNewGoalText] = useState("");
  const [adding, setAdding] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const loadGoals = async () => {
    const data = await getGoals(expertId, clientId);
    setGoals(data);
    setLoading(false);
  };

  useEffect(() => {
    if (expertId && clientId) {
      loadGoals();
    }
  }, [expertId, clientId]);

  const handleAddGoal = async () => {
    if (!newGoalText.trim()) return;
    setAdding(true);
    const result = await addGoal(expertId, clientId, newGoalText.trim());
    if (result) {
      setGoals((prev) => [...prev, result]);
      setNewGoalText("");
      setShowInput(false);
    }
    setAdding(false);
  };

  const handleToggle = async (goal) => {
    const updated = await toggleGoal(goal.id, !goal.is_completed);
    if (updated) {
      setGoals((prev) => prev.map((g) => g.id === goal.id ? updated : g));
    }
  };

  const handleDelete = async (goalId) => {
    const success = await deleteGoal(goalId);
    if (success) {
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
    }
  };

  if (loading) {
    return (
      <div id="progress-bar_loading" className="p-6 rounded-md bg-neutral-900 border border-neutral-800">
        <p className="text-lg font-extralight text-neutral-400">Загрузка целей...</p>
      </div>
    );
  }

  if (goals.length === 0 && !isExpertView) return null;

  const completed = goals.filter((g) => g.is_completed).length;
  const total = goals.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <motion.div
      id="progress-bar_container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 rounded-md bg-neutral-900 border border-neutral-800"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-primary-400" />
          <h3 className="text-xl font-medium text-white">
            {isExpertView ? "Цели клиента" : `Мой прогресс с ${expertName}`}
          </h3>
        </div>
        {isExpertView && (
          <button
            onClick={() => setShowInput(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="text-lg font-extralight">Добавить цель</span>
          </button>
        )}
      </div>

      {total > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-lg font-extralight text-neutral-400">
              {completed} из {total} ({percent}%)
            </span>
            {percent === 100 && (
              <span className="text-lg font-extralight text-accent-400">
                Все цели достигнуты!
              </span>
            )}
          </div>
          <div className="h-2 rounded-full bg-neutral-800 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${percent}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500"
            />
          </div>
        </div>
      )}

      {showInput && isExpertView && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-md bg-neutral-800/50 border border-neutral-700">
          <input
            type="text"
            value={newGoalText}
            onChange={(e) => setNewGoalText(e.target.value)}
            placeholder="Новая цель для клиента..."
            className="flex-1 px-3 py-2 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500"
            onKeyDown={(e) => e.key === "Enter" && handleAddGoal()}
            autoFocus
          />
          <button
            onClick={handleAddGoal}
            disabled={adding || !newGoalText.trim()}
            className="px-4 py-2 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 text-white text-lg font-extralight transition-colors"
          >
            {adding ? "..." : "Добавить"}
          </button>
          <button
            onClick={() => { setShowInput(false); setNewGoalText(""); }}
            className="p-2 rounded-md hover:bg-neutral-700 text-neutral-400 transition-colors"
          >
            <span className="text-lg font-extralight">Отмена</span>
          </button>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="text-center py-8">
          <Target className="w-10 h-10 text-neutral-700 mx-auto mb-3" />
          <p className="text-lg font-extralight text-neutral-500">
            {isExpertView ? "Добавьте цели для работы с клиентом" : "Эксперт пока не добавил целей"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {goals.map((goal) => (
            <div
              key={goal.id}
              className={`flex items-center gap-3 p-3 rounded-md transition-colors ${
                goal.is_completed
                  ? "bg-accent-500/5 border border-accent-500/20"
                  : "bg-neutral-800/30 border border-neutral-800"
              }`}
            >
              {isExpertView ? (
                <button onClick={() => handleToggle(goal)} className="flex-shrink-0">
                  {goal.is_completed ? (
                    <CheckCircle className="w-5 h-5 text-accent-400" />
                  ) : (
                    <Circle className="w-5 h-5 text-neutral-600 hover:text-neutral-400 transition-colors" />
                  )}
                </button>
              ) : (
                <div className="flex-shrink-0">
                  {goal.is_completed ? (
                    <CheckCircle className="w-5 h-5 text-accent-400" />
                  ) : (
                    <Circle className="w-5 h-5 text-neutral-600" />
                  )}
                </div>
              )}

              <span
                className={`flex-1 text-lg font-extralight ${
                  goal.is_completed ? "text-neutral-400 line-through" : "text-neutral-200"
                }`}
              >
                {goal.title}
              </span>

              {goal.is_completed && goal.completed_at && (
                <span className="text-lg font-extralight text-neutral-600 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(goal.completed_at).toLocaleDateString("ru-RU", {
                    day: "numeric",
                    month: "short"
                  })}
                </span>
              )}

              {isExpertView && (
                <button
                  onClick={() => handleDelete(goal.id)}
                  className="p-1 rounded hover:bg-red-500/10 text-neutral-600 hover:text-red-400 transition-colors flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
